import { PASSO_DA_GRADE_MINUTOS } from '@arena/shared/schemas';
import { horaDe, horaNoRelogio, MINUTOS_DO_DIA, minutosDe } from '@arena/shared/tempo';
import { situacaoDoHorario, type Janela, type TrechoOcupado } from './layout.js';

/**
 * O horário de começo escolhido com um clique — no lugar do `<select>` de
 * `#m-reserva` e do remarcar de `#m-evento`.
 *
 * Um botão por meia hora da janela de funcionamento, separados por turno.
 * O que está ocupado na quadra com a duração escolhida sai riscado e não
 * se clica; o que passaria do fechamento também não. É conforto: quem
 * decide é o servidor, que confere de novo ao salvar — a grade na tela
 * pode estar velha, e o perfil pode não enxergar toda marcação.
 *
 * O que já começou continua clicável, só mais apagado: a recepção lança a
 * locação de quem chegou às 19h e foi registrar às 19h10.
 *
 * As horas são as DA GRADE. Na arena que fecha às 02:00, o turno
 * "Madrugada" mostra 00:00, 00:30… e o valor é "24:00", "24:30" — a grade
 * de sábado seguindo pela noite. Quem grava traduz para o calendário
 * (`noCalendario`).
 */
export function SeletorDeHorario({
  janela,
  valor,
  duracaoMinutos,
  ocupados,
  agora,
  onChange,
  'aria-label': rotulo,
}: {
  janela: Janela;
  /** A hora da grade escolhida — "19:00", ou "25:00" na madrugada. */
  valor: string;
  duracaoMinutos: number;
  /** Os trechos ocupados da quadra escolhida, em minutos da grade. */
  ocupados: readonly TrechoOcupado[];
  /** Agora, em minutos da grade do dia escolhido: antes dele, o horário já passou. */
  agora: number;
  onChange: (hora: string) => void;
  'aria-label': string;
}) {
  const turnos = turnosDaJanela(janela);
  const escolhido = minutosDe(valor);

  return (
    <div className="horarios" role="group" aria-label={rotulo}>
      {turnos.map((turno) => (
        <div key={turno.nome} className="turno">
          <span className="turno-nome">
            {turno.nome}
            {turno.madrugada && <small> · dia seguinte</small>}
          </span>
          <div className="horarios-grade">
            {turno.inicios.map((inicio) => {
              const situacao = situacaoDoHorario(inicio, duracaoMinutos, janela, ocupados);
              const selecionado = inicio === escolhido;
              const passou = inicio < agora;
              const hora = horaNoRelogio(horaDe(inicio));
              return (
                <button
                  key={inicio}
                  type="button"
                  className={['horario', passou && 'passado', situacao !== 'livre' && situacao]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={selecionado}
                  aria-label={[hora, ROTULO_DA_SITUACAO[situacao], passou && 'já começou']
                    .filter(Boolean)
                    .join(', ')}
                  // O escolhido nunca se desabilita: mudar a duração pode
                  // fazê-lo colidir, e a pessoa precisa vê-lo marcado — o
                  // aviso de conflito embaixo diz o resto.
                  disabled={situacao !== 'livre' && !selecionado}
                  onClick={() => onChange(horaDe(inicio))}
                >
                  {hora}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

const ROTULO_DA_SITUACAO = {
  livre: null,
  ocupado: 'ocupado nesta quadra',
  'nao-cabe': 'passa do horário de fechar',
} as const;

interface Turno {
  readonly nome: string;
  readonly madrugada: boolean;
  /** Minutos da grade de cada começo, de meia em meia hora. */
  readonly inicios: readonly number[];
}

/**
 * Os começos da janela, de meia em meia hora até meia hora antes de
 * fechar, em turnos. A madrugada é o que passa da meia-noite — ela só
 * existe na arena que fecha depois dela.
 */
export function turnosDaJanela(janela: Janela): Turno[] {
  const turnos = [
    { nome: 'Manhã', ate: 12 * 60, madrugada: false },
    { nome: 'Tarde', ate: 18 * 60, madrugada: false },
    { nome: 'Noite', ate: MINUTOS_DO_DIA, madrugada: false },
    { nome: 'Madrugada', ate: Infinity, madrugada: true },
  ];
  const porTurno = turnos.map((t) => ({
    nome: t.nome,
    madrugada: t.madrugada,
    inicios: [] as number[],
  }));

  for (
    let m = janela.abertura;
    m + PASSO_DA_GRADE_MINUTOS <= janela.fechamento;
    m += PASSO_DA_GRADE_MINUTOS
  ) {
    const indice = turnos.findIndex((t) => m < t.ate);
    porTurno[indice]?.inicios.push(m);
  }
  return porTurno.filter((t) => t.inicios.length > 0);
}
